import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { settingsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import EmailTemplateForm from './EmailTemplateForm';
import EmptyState from '../common/EmptyState';

function GeneralEmailTemplates() {
  const [templates, setTemplates] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { showToast } = useToast();

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await settingsService.getAllEmailTemplates();
      setTemplates(response.data.emails || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
      showToast('Failed to load email templates', 'error');
      setTemplates([]);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await settingsService.updateEmailStatus({
        id,
        status: currentStatus ? 0 : 1
      });
      
      setTemplates(templates.map(template => 
        template.id === id 
          ? { ...template, status: currentStatus ? 0 : 1 } 
          : template
      ));
      
      showToast('Email template status updated successfully', 'success');
    } catch (error) {
      console.error('Error updating template status:', error);
      showToast('Failed to update template status', 'error');
    }
  };

  const handleOpenTemplate = async (id) => {
    try {
      const response = await settingsService.getEmailTemplate(id);
      if (response.data && response.data.approvalForm) {
        setSelectedTemplate(response.data.approvalForm);
        setOpenDialog(true);
      } else {
        showToast('Failed to load template details', 'error');
      }
    } catch (error) {
      console.error('Error fetching template details:', error);
      showToast('Failed to load template details', 'error');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
  };

  const handleSaveTemplate = async (formData) => {
    try {
      await settingsService.updateEmailTemplate(formData);
      showToast('Email template updated successfully', 'success');
      handleCloseDialog();
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      showToast('Failed to update template', 'error');
    }
  };

  return (
    <Box>
      {/*
      <Typography variant="h6" gutterBottom>
        All Email Templates
      </Typography>
      */}
      
      {templates !== null && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template Name</TableCell>
                {/*<TableCell>Type</TableCell>*/}
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length > 0 ? (
                templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      {template.name || "Unnamed Template"}
                      {/*
                      <Button 
                        color="primary"
                        onClick={() => handleOpenTemplate(template.id)}
                      >
                        {template.name || "Unnamed Template"}
                      </Button> */}
                    </TableCell>
                    {/*<TableCell>{template.type}</TableCell>*/}
                    <TableCell align="right">
                      <Switch
                        checked={template.status === 1}
                        onChange={() => handleToggleStatus(template.id, template.status)}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                    <EmptyState framed={false} title="No email templates found" message={null} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Edit Email Template
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <EmailTemplateForm 
              template={selectedTemplate} 
              onSave={handleSaveTemplate}
              onCancel={handleCloseDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default GeneralEmailTemplates;
